import React, { useState, useCallback, useEffect } from 'react'
import Breadcrumb, { BreadcrumbItem } from '@zera-admin/breadcrumb'
import Page, { PageHeader, Container } from '@zera-admin/page'
import Button from '@zera-admin/button'
import { uid } from '@zera-admin/utils'

import { useFlagcardContext } from 'app/contexts/flagcard'
import { usePopupContext } from 'app/contexts/popup'
import MainLayout from 'layouts/main'
import roleService, { Role } from 'services/http/identity-server/role'

import { UpdateRoleProps } from './types'
import RoleForm from './components/RoleForm'

const UpdateRole: React.FunctionComponent<UpdateRoleProps> = ({
  match,
  history,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [flagcard] = useFlagcardContext()
  const [popup] = usePopupContext()
  const [values, setValues] = useState({
    id: 0,
    title: '',
    name: '',
    claimIds: [] as number[],
  })

  const handleUpdate = async (data: Role) => {
    if (values) {
      setIsLoading(true)

      try {
        await roleService.update({ ...data, id: values.id })

        flagcard.add({
          title: 'Rol güncellendi',
          children: 'Geçerli rol başarıyla güncellendi.',
        })
      } catch {
        popup.add({
          title: 'Rol güncellenemedi',
          children:
            'Rol güncellenirken bir sorun oluştu, lütfen tekrar deneyin.',
        })
      }

      setIsLoading(false)
    }
  }

  const handleDelete = () => {
    const popupId = uid()

    const onClick = () => {
      if (values) {
        setIsLoading(true)

        roleService
          .del(values.id)
          .then(() => {
            flagcard.add({
              title: 'Rol silindi',
              children: 'Geçerli rol başarıyla silindi.',
            })

            history.push('/roles')
          })
          .catch(() => {
            popup.add({
              title: 'Rol silinemedi',
              children:
                'Rol silinirken bir sorun oluştu, lütfen sayfayı yenileyip tekrar deneyin.',
            })
          })
          .finally(() => {
            setIsLoading(false)
            popup.remove(popupId)
          })
      }
    }

    const onClose = () => {
      popup.remove(popupId)
    }

    popup.add({
      id: popupId,
      appearance: 'warning',
      title: 'Rol silinsin mi?',
      children:
        'Bu rolü silmek istediğinize emin misiniz? Bu işlem geri alınamaz.',
      actions: [
        {
          isLoading,
          onClick: onClick,
          text: 'Sil',
        },
        {
          onClick: onClose,
          text: 'Vazgeç',
        },
      ],
      onClose,
    })
  }

  const getValues = useCallback(async () => {
    const id = match.params.id

    try {
      const response = await roleService.getById(id)

      setValues(response.data)
    } catch {
      const popupId = uid()

      const onClose = () => {
        history.push('/roles')
        popup.remove(popupId)
      }

      popup.add({
        id: popupId,
        title: 'Rol getirilemedi',
        children:
          'Rol bilgisi getirilirken bi sorun oluştu, lütfen sayfayı yenileyip tekrar deneyin.',
        onClose,
        actions: [
          {
            appearance: 'primary',
            text: 'Geri dön',
            onClick: onClose,
          },
        ],
      })
    }

    setIsReady(true)
  }, [match.params.id, popup, history])

  useEffect(() => {
    getValues()
  }, [getValues])

  const breadcrumbs = (
    <Breadcrumb>
      <BreadcrumbItem text="Anasayfa" href="/" />
      <BreadcrumbItem text="Roller" href="/roles" />
      <BreadcrumbItem text="Rol detayı" />
    </Breadcrumb>
  )

  return (
    <MainLayout isLoading={!isReady}>
      <Container width="medium">
        <Page>
          <PageHeader
            breadcrumbs={breadcrumbs}
            actions={
              <Button
                appearance="error"
                isDisabled={isLoading}
                onClick={handleDelete}
              >
                Rolü sil
              </Button>
            }
            bottomBar={
              <p>
                Oluşturduğunuz rol bilgilerini aşağıdan güncelleyebilirsiniz.
              </p>
            }
          >
            Rol görüntüleniyor: {values?.title || '-'}
          </PageHeader>
          <RoleForm
            loading={isLoading}
            values={values}
            operation="update"
            onSubmit={handleUpdate}
          />
        </Page>
      </Container>
    </MainLayout>
  )
}

export default UpdateRole
